import React, {
  useState,
  useEffect,
  useCallback,
  useContext,
} from 'react';
import PropTypes from 'prop-types';
import * as Sentry from '@sentry/browser';

import useSessionStore from '../../../hooks/useSessionStore';
import useComponentMounted from '../../../hooks/useComponentMounted';
import useToast from '../../../CoachTools/hooks/useToast';
import User from '../../../Model/User';
import ChatMessage from '../../Model/ChatMessage';
import ChatChannelContext from '../../context/ChatChannelContext';
import CreateCoachTaskModal from '../../../CoachTools/layout/components/CreateCoachTaskModal';

import texts from './texts.json';

/**
 * Hosts the create-coach-task modal for a specific chat message (opened from the
 * message action menu). The created task links to the message — channelId + messageId
 * + a denormalized preview — so the coach can reply to it in context. The subject is
 * the channel's client and the coach is the channel's coach (resolved from the
 * active channel, not the message sender).
 */
const MessageTaskModal = ({ isOpen, onClose, message }) => {
  const { isAdmin, isCoachAssistant } = useSessionStore();
  const { activeChannel } = useContext(ChatChannelContext);
  const { showToast } = useToast();
  const isComponentMountedRef = useComponentMounted();

  const clientId = activeChannel?.userId;
  const coachId = activeChannel?.coachId;

  // Client + coach docs resolved on open (names/avatars feed the task's header).
  const [subject, setSubject] = useState(null);
  const [coach, setCoach] = useState(null);

  // Captured as a primitive so the effect below doesn't re-run when the ChatMessage document
  // identity changes (a MobX re-render can replace the reference) — it's only used for logging.
  const messageId = message?.id;

  useEffect(() => {
    if (!isOpen || !clientId || !coachId) {
      return;
    }
    (async () => {
      try {
        const [clientDoc, coachDoc] = await Promise.all([
          User.getById(clientId),
          User.getById(coachId),
        ]);
        if (!isComponentMountedRef.current) {
          return;
        }
        setSubject({ id: clientId, name: clientDoc?.name || '', avatar: clientDoc?.avatarUrl || '' });
        setCoach({ id: coachId, name: coachDoc?.name || '', avatar: coachDoc?.avatarUrl || '' });
      } catch (error) {
        Sentry.captureException(error, { extra: { clientId, coachId, messageId } });
      }
    })();
  }, [isOpen, clientId, coachId, isComponentMountedRef, messageId]);

  const onCreated = useCallback(() => showToast(texts.taskCreated), [showToast]);
  const onError = useCallback(
    (errorMessage = '') => showToast(errorMessage || texts.taskError, { error: true }),
    [showToast],
  );

  // Internal staff only, and only when the channel resolves to a real client/coach pair.
  const canCreate = (isAdmin || isCoachAssistant) && !!clientId && !!coachId && clientId !== coachId;
  if (!canCreate || !subject || !coach) {
    return null;
  }

  // Denormalized so task surfaces (mobile detail, web feed) render the linked message
  // without fetching it; full attachment objects are stored so quoted-reply and mobile
  // attachment strips can render without re-fetching the original message.
  const messagePreview = {
    text: message.text || '',
    senderUserId: message.userId || '',
    senderName: message.userName || '',
    attachments: message.attachments || [],
    // Carry internal (coach-only) text so feed-armed quoted replies match the direct-reply flow
    // (ReplyMessagePreview's "has internal text" warning + ChannelMessageInput's parent.internalText).
    ...(message.internalText && { internalText: message.internalText }),
  };

  return (
    <CreateCoachTaskModal
      isOpen={isOpen}
      onDidDismiss={onClose}
      onCreated={onCreated}
      onError={onError}
      subjectId={subject.id}
      subjectName={subject.name}
      subjectAvatar={subject.avatar}
      coachId={coach.id}
      coachName={coach.name}
      coachAvatar={coach.avatar}
      channelId={message.channelId}
      messageId={message.id}
      messagePreview={messagePreview}
    />
  );
};

MessageTaskModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  message: PropTypes.instanceOf(ChatMessage).isRequired,
};

export default MessageTaskModal;
